/**
 * RuleAnalyticsPage Component
 *
 * Detailed analytics page for a single rule showing trend, provider breakdown, and violations
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useParams } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import { useCreateTaskFromRuleMutation } from '@features/rule-build';
import { rulesApi, useDisableRuleMutation, useEnableRuleMutation, useGetRuleQuery } from '@features/rules';
import { useCan } from '@features/user';
import {
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Fade,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch , useModal } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { ComplianceTrendChart } from './components/dashboard/ComplianceTrendChart';
import { ProviderBreakdownTable } from './components/dashboard/ProviderBreakdownTable';
import { JotAuditPageLayout } from './components/layout';
import { RulesAnalyticsFilters, type RulesAnalyticsFilters as RulesAnalyticsFiltersType } from './components/rules/RulesAnalyticsFilters';
import { useRuleDetailStats } from './hooks/useRuleDetailStats';

export const RuleAnalyticsPage: React.FC = () => {
  const { ruleId, organizationId } = useParams<{ ruleId: string; organizationId?: string }>();
  const navigate = useNavigate();
  const { open: openModal } = useModal();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { jotauditDashboard } = useEventTracking();
  // ADR 0073 Phase 3 (GDN-1590): parity with hasRole('practice_administrator').
  const isPracticeAdmin = useCan('manage_members');

  const [isRuleExpanded, setIsRuleExpanded] = useState(false);
  const [filters, setFilters] = useState<RulesAnalyticsFiltersType>({
    comparisonMode: false,
  });
  const viewedTrackedRef = useRef<string | null>(null);
  const previousFiltersRef = useRef<RulesAnalyticsFiltersType>(filters);

  // Convert userIds array to comma-separated string for the API
  const userIdsString = useMemo(() => {
    if (!filters.userIds || filters.userIds.length === 0) return undefined;
    return filters.userIds.join(',');
  }, [filters.userIds]);

  const {
    data,
    overall,
    trend,
    trendGranularity,
    byProvider,
    ruleBodyMd,
    ruleName,
    isLoading,
    error,
    refetch,
  } = useRuleDetailStats({
    ruleId: ruleId || '',
    organizationId,
    skip: !ruleId,
    days: filters.days,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    userIds: userIdsString,
  });

  // Fetch rule details for edit/disable functionality
  const { data: ruleData } = useGetRuleQuery(ruleId || '', { skip: !ruleId });
  const [disableRule, { isLoading: isDisabling }] = useDisableRuleMutation();
  const [enableRule, { isLoading: isEnabling }] = useEnableRuleMutation();
  const [createTaskFromRule, { isLoading: isCreatingTask }] = useCreateTaskFromRuleMutation();

  const isDisabled = ruleData?.is_disabled ?? false;
  const canEdit = ruleData && !ruleData.is_global;
  const canDisable = isPracticeAdmin && ruleData?.rule_type === 'compliance' && !isDisabled;
  const canEnable = isPracticeAdmin && ruleData?.rule_type === 'compliance' && isDisabled;

  const handleBack = useCallback(() => {
    if (organizationId) {
      navigate(`/admin-v2/jotaudit/${organizationId}?tab=rules`);
    } else {
      navigate('/jotaudit?tab=rules');
    }
  }, [navigate, organizationId]);

  const handleViewViolations = useCallback(() => {
    if (!ruleId) return;

    // Track view violations click
    jotauditDashboard.viewViolationsClicked({
      rule_id: ruleId,
      violation_count: overall?.non_compliant_count || 0,
    });

    openModal(MODAL_TYPES.RULE_DRILLDOWN, {
      ruleId,
      complianceStatus: 'no',
    });
  }, [ruleId, overall, openModal, jotauditDashboard]);

  const handleProviderClick = useCallback(
    (userId: string, complianceRate?: number) => {
      // Track provider click from analytics
      jotauditDashboard.analyticsProviderClicked({
        rule_id: ruleId,
        provider_id: userId,
        provider_compliance_rate: complianceRate,
      });

      navigate(`/jotaudit/provider/${userId}`);
    },
    [navigate, ruleId, jotauditDashboard],
  );

  const handleEditRule = useCallback(async () => {
    if (!ruleId) return;

    // Track rule edit click
    jotauditDashboard.ruleEditClicked({
      rule_id: ruleId,
      source: 'analytics_page',
    });

    try {
      // Create a task from the existing rule
      const result = await createTaskFromRule({ ruleId }).unwrap();
      
      // Navigate to the rule-build page with the new task
      navigate(`/rule-build/${result.task_id}`);
    } catch (error) {
      console.error('Failed to create task from rule:', error);
      enqueueSnackbar('Failed to load rule editor. Please try again.', { variant: 'error' });
    }
  }, [navigate, ruleId, jotauditDashboard, createTaskFromRule, enqueueSnackbar]);

  const handleDisableRule = useCallback(async () => {
    if (!ruleId) return;

    try {
      await disableRule({
        ruleId,
        providerRuleDisableRequest: {
          scope: 'org',
        },
      }).unwrap();

      // Track rule disabled
      jotauditDashboard.ruleDisabled({
        rule_id: ruleId,
        source: 'analytics_page',
      });

      // Invalidate cache to refresh rule data
      dispatch(
        rulesApi.util.invalidateTags([
          { type: 'Rule', id: ruleId },
          { type: 'RuleList', id: 'LIST' },
        ]),
      );
      enqueueSnackbar('Rule disabled successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to disable rule:', error);
      enqueueSnackbar('Failed to disable rule', { variant: 'error' });
    }
  }, [ruleId, disableRule, dispatch, enqueueSnackbar, jotauditDashboard]);

  const handleEnableRule = useCallback(async () => {
    if (!ruleId) return;

    try {
      await enableRule({
        ruleId,
        providerRuleDisableRequest: {
          scope: 'org',
        },
      }).unwrap();

      // Track rule enabled
      jotauditDashboard.ruleEnabled({
        rule_id: ruleId,
        source: 'analytics_page',
      });

      // Invalidate cache to refresh rule data
      dispatch(
        rulesApi.util.invalidateTags([
          { type: 'Rule', id: ruleId },
          { type: 'RuleList', id: 'LIST' },
        ]),
      );
      enqueueSnackbar('Rule enabled successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to enable rule:', error);
      enqueueSnackbar('Failed to enable rule', { variant: 'error' });
    }
  }, [ruleId, enableRule, dispatch, enqueueSnackbar, jotauditDashboard]);

  const hasViolations = useMemo(() => {
    return (overall?.non_compliant_count || 0) > 0;
  }, [overall]);

  // Check if rule content is long enough to need truncation
  const needsTruncation = useMemo(() => {
    if (!ruleBodyMd) return false;
    // Rough estimate: if markdown has more than ~500 characters, it likely needs truncation
    return ruleBodyMd.length > 500;
  }, [ruleBodyMd]);

  // Track page viewed
  useEffect(() => {
    if (ruleId && overall && viewedTrackedRef.current !== ruleId) {
      viewedTrackedRef.current = ruleId;
      jotauditDashboard.ruleAnalyticsViewed({
        rule_id: ruleId,
        rule_name: ruleName || ruleId,
        total_audits: overall.total_audits || 0,
        compliance_rate: overall.compliance_rate ?? undefined,
        violation_count: overall.non_compliant_count || 0,
        is_disabled: isDisabled,
      });
    }
  }, [ruleId, overall, ruleName, isDisabled, jotauditDashboard]);

  // Track rule content expansion
  const handleToggleRuleExpanded = useCallback(() => {
    const newExpandedState = !isRuleExpanded;
    setIsRuleExpanded(newExpandedState);
    
    jotauditDashboard.ruleContentExpanded({
      rule_id: ruleId,
      expanded: newExpandedState,
    });
  }, [isRuleExpanded, ruleId, jotauditDashboard]);

  // Track filter changes
  useEffect(() => {
    const prev = previousFiltersRef.current;
    
    // Check if filters changed (skip initial mount)
    if (prev !== filters && (prev.days !== filters.days || prev.dateFrom !== filters.dateFrom || 
        prev.dateTo !== filters.dateTo || (prev.userIds?.join(',') !== filters.userIds?.join(',')))) {
      
      let filterType: 'days' | 'date_range' | 'clinicians' = 'days';
      let previousValue = '';
      let newValue = '';

      if (prev.days !== filters.days) {
        filterType = 'days';
        previousValue = prev.days?.toString() || 'none';
        newValue = filters.days?.toString() || 'none';
      } else if (prev.dateFrom !== filters.dateFrom || prev.dateTo !== filters.dateTo) {
        filterType = 'date_range';
        previousValue = prev.dateFrom ? `${prev.dateFrom}_${prev.dateTo}` : 'none';
        newValue = filters.dateFrom ? `${filters.dateFrom}_${filters.dateTo}` : 'none';
      } else if (prev.userIds?.join(',') !== filters.userIds?.join(',')) {
        filterType = 'clinicians';
        previousValue = (prev.userIds?.length || 0).toString();
        newValue = (filters.userIds?.length || 0).toString();
      }

      jotauditDashboard.analyticsFilterChanged({
        rule_id: ruleId,
        filter_type: filterType,
        previous_value: previousValue,
        new_value: newValue,
      });
    }

    previousFiltersRef.current = filters;
  }, [filters, ruleId, jotauditDashboard]);

  // Handle errors
  if (error) {
    // Show friendly empty state for "no data yet" case
    if (error === 'no_data_yet') {
      return (
        <JotAuditPageLayout>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={handleBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                {ruleName || ruleId || 'Rule Analytics'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compliance analytics and trend analysis
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canEdit && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEditRule}
                  disabled={isCreatingTask}
                  sx={{ textTransform: 'none' }}
                >
                  {isCreatingTask ? 'Loading...' : 'Edit Rule'}
                </Button>
              )}
            </Box>
          </Box>

          {/* No Data Alert */}
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'info.light',
            }}
          >
            <AlertTitle>No audit data available yet</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This rule hasn't been evaluated in any audits yet. Analytics will appear here once:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
              <li>Notes are created and audited with this rule enabled</li>
              <li>The audit system processes the notes</li>
              <li>Results are aggregated (usually within a few hours)</li>
            </Box>
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 500 }}>
              Check back later to see compliance trends and insights.
            </Typography>
          </Alert>
        </JotAuditPageLayout>
      );
    }

    // Show error alert for other errors
    return (
      <JotAuditPageLayout>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refetch}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Error loading rule analytics</AlertTitle>
          {error}
        </Alert>
      </JotAuditPageLayout>
    );
  }

  return (
    <JotAuditPageLayout>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 0.5,
              textDecoration: overall?.is_deleted ? 'line-through' : 'none',
              color: overall?.is_disabled
                ? theme.palette.text.disabled
                : overall?.is_deleted
                  ? theme.palette.text.secondary
                  : 'inherit',
              opacity: overall?.is_disabled ? 0.6 : 1,
            }}
          >
            {isLoading ? 'Loading...' : ruleName || ruleId || 'Rule Analytics'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compliance analytics and trend analysis
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditRule}
              disabled={isCreatingTask}
              sx={{ textTransform: 'none' }}
            >
              {isCreatingTask ? 'Loading...' : 'Edit Rule'}
            </Button>
          )}
          {canDisable && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<BlockIcon />}
              onClick={handleDisableRule}
              disabled={isDisabling}
              sx={{ textTransform: 'none' }}
            >
              {isDisabling ? 'Disabling...' : 'Disable Rule'}
            </Button>
          )}
          {canEnable && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleEnableRule}
              disabled={isEnabling}
              sx={{ textTransform: 'none' }}
            >
              {isEnabling ? 'Enabling...' : 'Enable Rule'}
            </Button>
          )}
          {hasViolations && (
            <Button
              variant="contained"
              color="error"
              startIcon={<WarningIcon />}
              onClick={handleViewViolations}
              sx={{ textTransform: 'none' }}
            >
              View Violations
            </Button>
          )}
        </Box>
      </Box>

      {/* Rule Content */}
      {ruleBodyMd && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Rule Content
          </Typography>
          <Box
            sx={{
              position: 'relative',
              maxHeight: isRuleExpanded ? 'none' : '200px',
              overflow: 'hidden',
              '& p': { mb: 1 },
              '& ul, & ol': { mb: 1, pl: 2 },
              '& h1, & h2, & h3': { mt: 2, mb: 1 },
            }}
          >
            <ReactMarkdown>{ruleBodyMd}</ReactMarkdown>
            {!isRuleExpanded && needsTruncation && (
              <Fade in={!isRuleExpanded}>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '80px',
                    background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
                    pointerEvents: 'none',
                  }}
                />
              </Fade>
            )}
          </Box>
          {needsTruncation && (
            <Button
              variant="text"
              size="small"
              onClick={handleToggleRuleExpanded}
              sx={{
                mt: 1,
                textTransform: 'none',
                color: 'primary.main',
              }}
            >
              {isRuleExpanded ? 'Show Less' : 'Show More'}
            </Button>
          )}
        </Paper>
      )}

      {/* Filters */}
      <RulesAnalyticsFilters
        filters={filters}
        onFiltersChange={setFilters}
        isLoading={isLoading}
        showComparisonMode={false}
      />

      {/* Overall Stats Summary */}
      {overall && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              flex: 1,
              minWidth: 150,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Total Audits
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {overall.total_audits || 0}
            </Typography>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              flex: 1,
              minWidth: 150,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Compliance Rate
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {overall.compliance_rate?.toFixed(1) || 0}%
            </Typography>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              flex: 1,
              minWidth: 150,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Violations
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
              {overall.non_compliant_count || 0}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Trend Chart */}
      {trend && trend.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <ComplianceTrendChart
            trend={trend}
            trendGranularity={trendGranularity}
            isLoading={isLoading}
          />
        </Box>
      )}

      {/* Provider Breakdown */}
      {byProvider && byProvider.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Provider Breakdown
          </Typography>
          <ProviderBreakdownTable
            byProvider={byProvider}
            isLoading={isLoading}
            onProviderClick={handleProviderClick}
          />
        </Box>
      )}
    </JotAuditPageLayout>
  );
};

